import React, { useMemo } from "react";
import { useSearchGroup } from "./hooks";
import { getDeepValue } from "./hooks/useQuery/utils";
import moment from "moment";
import type { KeyPath, SourceObject } from "./hooks/useQuery/dto";
import type { SearchItemProps } from "./dto";

/** 搜索控件包装器 */
const SearchItem = <T extends SourceObject = any>(props: SearchItemProps) => {
  const {
    keyPath,
    valueKey = "value",
    trigger = "onChange",
    targetKey = "value",
    children,
  } = props;

  const { query, dispatch, submit } = useSearchGroup<T>();

  const childElement = React.Children.only(children);

  const innerOnChange = (...rest) => {
    if (Array.isArray(valueKey)) {
      //如果指定多个value属性,则按索引从rest里取值
      const arr: [KeyPath, any][] = (keyPath as KeyPath[]).map((n, i) => {
        if (typeof rest[i] === "object") {
          return [n, rest[i]?.["target"]?.[targetKey]];
        }
        return [n, rest[i]];
      });
      const nameMap = new Map(arr);
      dispatch(nameMap);
    } else {
      //如果valueKey为单字符串，则认为默认取第一个值作为value
      let [v] = rest;
      if (moment.isMoment(v)) {
        v = moment(v).format("YYYY-MM-DD HH:mm:ss");
      } else if (Array.isArray(v)) {
        v = v.map((o) =>
          moment.isMoment(o) ? moment(o).format("YYYY-MM-DD HH:mm:ss") : o
        );
      } else if (typeof v === "object") {
        v = v?.["target"]?.[targetKey];
      }
      dispatch(new Map([[keyPath as KeyPath, v]]));
    }
    const originTriggerFunc: any = childElement.props[trigger];
    if (originTriggerFunc) {
      originTriggerFunc(...rest);
    }
  };

  const values = useMemo(() => {
    const obj = {};
    if (
      Array.isArray(keyPath) &&
      keyPath.some((m) => Array.isArray(m)) &&
      Array.isArray(valueKey)
    ) {
      keyPath.forEach((m, i) => (obj[valueKey[i]] = getDeepValue(query, m)));
    } else {
      obj[valueKey as string] = getDeepValue(query, keyPath as KeyPath);
    }

    return obj;
  }, [query, keyPath, valueKey]);

  return React.isValidElement(children)
    ? React.cloneElement<any>(children, {
        ...values,
        onSubmit: submit,
        [trigger]: innerOnChange,
      })
    : children;
};

export default SearchItem;
